/**
 * Offres d'abonnement.
 *
 * Ensembles fermés partagés entre la base (pgEnum), le serveur (zod) et le front (libellés i18n).
 * Règle : valeurs ASCII en snake_case, jamais de libellé humain ici. Ce fichier est réexporté par
 * `../enums.js`, qui reste le seul point d'entrée : personne n'importe un domaine directement.
 */

import { assignableMemberRoles, type AssignableMemberRole, type MemberRole } from './identity.js';

/**
 * Les trois offres, **dans l'ordre croissant**. L'ordre porte du sens : `planRank` s'en sert pour
 * dire si une offre en couvre une autre, ce qui évite d'écrire à la main quinze comparaisons.
 *
 * Les clés reprennent celles de la V1, en français comme `consultationReasonCategories`.
 *
 * L'offre gratuite de la V1 n'existe plus, décision de Nicolas du 26 septembre 2026 : un cabinet
 * neuf reçoit **30 jours d'essai de l'offre Professionnel** (`TRIAL_DAYS`, statut `trialing`),
 * puis passe en lecture seule (`expired`). Entrer sans payer reste possible, mais avec une date
 * de fin, et jamais au prix d'héberger sans limite des dossiers médicaux d'un cabinet qui ne paie
 * pas.
 */
export const subscriptionPlans = ['essentiel', 'professionnel', 'cabinet'] as const;
export type SubscriptionPlan = (typeof subscriptionPlans)[number];

/** Durée de l'essai d'un cabinet neuf, en jours. Une seule source, lue par le serveur et le front. */
export const TRIAL_DAYS = 30;

/**
 * Conservation en lecture seule après un essai non converti, en jours, à compter de la fin de
 * l'essai. Décision de Nicolas du 27 septembre 2026 : 90 jours, puis effacement.
 */
export const TRIAL_RETENTION_DAYS = 90;

/** Report d'effacement posé par la plateforme, en jours, ajoutés à la date d'effacement. */
export const TRIAL_DELETION_POSTPONE_DAYS = 30;

/** Paliers de courriel avant effacement, en jours avant la date dite. */
export const TRIAL_DELETION_NOTICE_DAYS = [30, 7] as const;

/** L'offre ouverte pendant l'essai : le prospect voit le produit entier. */
export const TRIAL_PLAN: SubscriptionPlan = 'professionnel';

/**
 * Dernier courriel d'effacement parti pour un essai non converti.
 *
 * `none` : rien encore. `trial_ended` : entrée en lecture seule. `day_30` / `day_7` : avertissements
 * recalculés si la date d'effacement est reportée — le palier `trial_ended` ne repart jamais.
 */
export const deletionNoticeStages = ['none', 'trial_ended', 'day_30', 'day_7'] as const;
export type DeletionNoticeStage = (typeof deletionNoticeStages)[number];

/**
 * Dernier rappel parti avant la fin d'un essai en cours.
 *
 * `none` : rien encore. `day7` : J-7. `day1` : J-1. Même ordre que la migration 0077.
 */
export const trialReminderStages = ['none', 'day7', 'day1'] as const;
export type TrialReminderStage = (typeof trialReminderStages)[number];

/**
 * Motifs d'effacement d'un cabinet : essai non converti, ou abonnement payé arrivé à son terme
 * (même parcours depuis le 30 septembre 2026, migration 0081).
 */
export const organizationErasureReasons = ['unconverted_trial', 'subscription_ended'] as const;
export type OrganizationErasureReason = (typeof organizationErasureReasons)[number];

/** Mensuel ou annuel. L'annuel est remisé ; les deux montants vivent dans le catalogue. */
export const billingPeriods = ['monthly', 'annual'] as const;
export type BillingPeriod = (typeof billingPeriods)[number];

/**
 * État de l'abonnement d'un cabinet.
 *
 * `trialing` : essai en cours, l'offre d'essai est entièrement ouverte jusqu'à `currentPeriodEnd`.
 * `active` : à jour. `past_due` : un paiement a échoué, l'accès reste ouvert le temps de
 * régulariser — couper l'accès à un dossier médical parce qu'une carte a expiré serait
 * disproportionné. `expired` : l'essai est arrivé à son terme ou l'abonnement a pris fin. Le
 * cabinet passe en **lecture seule** : ses membres se connectent, lisent et exportent, mais plus
 * rien ne s'écrit. Après un essai non converti, les données restent 90 jours puis sont effacées
 * (`TRIAL_RETENTION_DAYS`) ; l'export reste possible pendant ce délai.
 */
export const subscriptionStatuses = ['trialing', 'active', 'past_due', 'expired'] as const;
export type SubscriptionStatus = (typeof subscriptionStatuses)[number];

/**
 * Quels rôles consomment un siège de praticien.
 *
 * Une secrétaire n'en consomme pas. Un remplaçant non plus : il n'exerce qu'à la place de
 * quelqu'un, le temps d'un remplacement daté. Le quota porte sur les praticiens permanents.
 *
 * Table totale et non un `filter` : un rôle de plus ne compilera pas tant que la question « est-ce
 * qu'il consomme un siège » n'aura pas reçu de réponse.
 */
export const roleConsumesPractitionerSeat: Readonly<Record<MemberRole, boolean>> = {
  owner: true,
  collaborateur: true,
  remplacant: false,
  salarie: true,
  secretary: false,
};

/** Collaborateur et salarié : praticiens permanents, offre Cabinet seulement. */
export function isMultiPractitionerRole(role: MemberRole): role is 'collaborateur' | 'salarie' {
  return role === 'collaborateur' || role === 'salarie';
}

/** L'offre laisse-t-elle ce rôle exercer ? Propriétaire, remplaçant et secrétaire : toutes. */
export function planAllowsRole(plan: SubscriptionPlan, role: MemberRole): boolean {
  if (isMultiPractitionerRole(role)) return plan === 'cabinet';
  return true;
}

/**
 * Compte Doctena par praticien : offre Cabinet seulement, le même cran que l'équipe permanente
 * (collaborateur et salarié). Essentiel et Professionnel restent sur un seul compte.
 */
export function planAllowsDoctenaMemberMode(plan: SubscriptionPlan): boolean {
  return plan === 'cabinet';
}

/** Rôles proposés à l'invitation et au changement, pour cette offre. */
export function rolesAllowedIn(plan: SubscriptionPlan): AssignableMemberRole[] {
  return assignableMemberRoles.filter((role) => planAllowsRole(plan, role));
}

/** Première offre qui laisse exercer ce rôle, pour le message « à partir de Cabinet ». */
export function firstPlanAllowingRole(role: MemberRole): SubscriptionPlan | null {
  return subscriptionPlans.find((plan) => planAllowsRole(plan, role)) ?? null;
}

/**
 * Les quatre fonctionnalités qui distinguent les offres, arbitrage du 20 septembre 2026 : « toutes
 * les fonctionnalités pour toutes les offres, sauf » celles-ci. Tout le reste est ouvert partout,
 * et c'est volontaire — un verrou de plus est une promesse de moins.
 *
 * `configurableDashboard` s'y ajoute comme cinquième, d'abord propre à l'offre Cabinet puis
 * rattaché à Professionnel (décision de Nicolas, 25 septembre 2026 : « tableau de bord
 * personnalisé pour offre Pro et Cabinet »), et `weroAutoReconciliation` comme sixième, rattaché à
 * Professionnel comme les quatre premiers (même jour) : le QR de facture Wero reste ouvert à toutes
 * les offres, seul le rapprochement automatique des paiements — clé d'API, notification, tâche —
 * est réservé.
 */
export const planFeatures = [
  'clinicalTests',
  'waltonDiagram',
  'aiMedicalLetters',
  'prioritySupport',
  'configurableDashboard',
  'weroAutoReconciliation',
] as const;
export type PlanFeature = (typeof planFeatures)[number];
